export interface HazardSpawnerDeps {
  settings: {
    oilBarrels: boolean;
    oilBarrelCount: { min: number; max: number };
    prePlacedMines: boolean;
    prePlacedMineCount: { min: number; max: number };
  };
  terrain: { isSolid: (x: number, y: number) => boolean };
  terrainWidth: number;
  terrainHeight: number;
  waterLevel: number;
  getWorms: () => { position: { x: number; y: number } }[];
  addBarrel: (x: number, y: number) => void;
  addMine: (x: number, y: number) => void;
  getRNG: () => {
    nextInt: (min: number, max: number) => number;
    nextFloat: (min: number, max: number) => number;
    next: () => number;
  };
}

export class HazardSpawner {
  private readonly MIN_WORM_DISTANCE = 60;
  private readonly MAX_SPAWN_ATTEMPTS = 50;
  private readonly EDGE_MARGIN = 100;
  private readonly TOP_MARGIN = 50;
  private readonly BOTTOM_MARGIN = 50;

  constructor(private deps: HazardSpawnerDeps) {}

  spawnInitialHazards(): void {
    const { settings } = this.deps;
    const rng = this.deps.getRNG();

    if (settings.oilBarrels) {
      const { min, max } = settings.oilBarrelCount;
      const numBarrels = rng.nextInt(min, max);

      for (let i = 0; i < numBarrels; i++) {
        const pos = this.findRandomTerrainPosition();
        if (pos) {
          this.deps.addBarrel(pos.x, pos.y);
        }
      }
    }

    if (settings.prePlacedMines) {
      const { min, max } = settings.prePlacedMineCount;
      const numMines = rng.nextInt(min, max);

      for (let i = 0; i < numMines; i++) {
        const pos = this.findRandomTerrainPosition();
        if (pos) {
          this.deps.addMine(pos.x, pos.y);
        }
      }
    }
  }

  findRandomTerrainPosition(): { x: number; y: number } | null {
    const rng = this.deps.getRNG();
    const maxY = this.deps.waterLevel - this.BOTTOM_MARGIN;

    for (let i = 0; i < this.MAX_SPAWN_ATTEMPTS; i++) {
      const x = rng.nextFloat(this.EDGE_MARGIN, this.deps.terrainWidth - this.EDGE_MARGIN);

      for (let y = this.TOP_MARGIN; y < maxY; y++) {
        if (this.deps.terrain.isSolid(x, y) && !this.deps.terrain.isSolid(x, y - 1)) {
          const tooCloseToWorm = this.deps.getWorms().some((w) => {
            const dx = w.position.x - x;
            const dy = w.position.y - y;
            return Math.sqrt(dx * dx + dy * dy) < this.MIN_WORM_DISTANCE;
          });

          if (!tooCloseToWorm) {
            return { x, y: y - 1 };
          }
          break;
        }
      }
    }
    return null;
  }
}
